/**
 * Tool Display Registry
 *
 * Maps tool names to their display components.
 * This replaces the manual switch/case logic in Message.tsx.
 *
 * New tools can be added by:
 * 1. Creating a display component in the appropriate category folder
 * 2. Adding an entry to this registry
 */

import type { ComponentType } from "react";
import type { AggregatedToolCall, ToolDisplayProps, ToolName } from "@/lib/sola/types/tools";

// Import generic displays
import { SimpleDisplay } from "./generic/SimpleDisplay";
import { OperationSuccessDisplay } from "./generic/OperationSuccessDisplay";
import { ListResultsDisplay } from "./generic/ListResultsDisplay";

// Import category-specific displays
import { QueryDataDisplay } from "./analytics/QueryDataDisplay";
import { ExportAnalyticsDisplay } from "./analytics/ExportAnalyticsDisplay";
import { PlanReportDisplay } from "./analytics/PlanReportDisplay";
import { ReportChartDisplay } from "./analytics/ReportChartDisplay";

import { JudgeResultDisplay } from "./judge/JudgeResultDisplay";

import { SearchCandidatesDisplay } from "./candidates/SearchCandidatesDisplay";
import { CandidateDetailDisplay } from "./candidates/CandidateDetailDisplay";
import { CandidateOperationDisplay } from "./candidates/CandidateOperationDisplay";
import { ResumeGeneratorDisplay } from "./candidates/ResumeGeneratorDisplay";

import { SearchContactsDisplay } from "./contacts/SearchContactsDisplay";
import { ContactDetailDisplay } from "./contacts/ContactDetailDisplay";
import { ContactOperationDisplay } from "./contacts/ContactOperationDisplay";

import { SearchFloatsDisplay } from "./floats/SearchFloatsDisplay";
import { FloatDetailDisplay } from "./floats/FloatDetailDisplay";
import { FloatOperationDisplay } from "./floats/FloatOperationDisplay";

import { EmailDraftDisplay } from "./email/EmailDraftDisplay";
import { EmailCampaignDisplay } from "./email/EmailCampaignDisplay";

import { DashboardStatsDisplay } from "./dashboard/DashboardStatsDisplay";
import { TopLeadsDisplay } from "./dashboard/TopLeadsDisplay";

import { IntegrationStatusDisplay } from "./integrations/IntegrationStatusDisplay";

import { TeamDataDisplay } from "./team/TeamDataDisplay";

import { WebSearchDisplay } from "./research/WebSearchDisplay";

import { NotificationsDisplay } from "./notifications/NotificationsDisplay";

import { QuickRepliesDisplay } from "./suggestions/QuickRepliesDisplay";
import { isPendingSend } from "./email/SendConfirmationCard";

/**
 * Registry entry type
 */
interface RegistryEntry {
  component: ComponentType<ToolDisplayProps> | (() => Promise<{ default: ComponentType<any> }>);
  lazy?: boolean;
  /**
   * Show the rich display even when debug mode is OFF: always (true), or only
   * for some results (e.g. a send waiting for the recruiter's confirmation).
   */
  alwaysShowRich?: boolean | ((toolCall: AggregatedToolCall) => boolean);
}

/**
 * Tool display registry
 *
 * Maps tool names to their display components.
 * Uses generic displays as defaults with specialized displays where available.
 */
export const toolDisplayRegistry: Partial<Record<ToolName | string, RegistryEntry>> = {
  // ============== Analytics ==============
  queryData: { component: QueryDataDisplay },
  exportAnalyticsData: { component: ExportAnalyticsDisplay },
  planReport: { component: PlanReportDisplay },
  renderReportChart: { component: ReportChartDisplay, alwaysShowRich: true },

  // ============== Candidates - Core ==============
  // The matches answer the question, so show them even in normal mode.
  searchCandidates: { component: SearchCandidatesDisplay, alwaysShowRich: true },
  matchCandidatesToJob: { component: SearchCandidatesDisplay, alwaysShowRich: true },
  getCandidate: { component: CandidateDetailDisplay },
  createCandidate: { component: CandidateOperationDisplay },
  deleteCandidate: { component: CandidateOperationDisplay },
  updateCandidateData: { component: CandidateOperationDisplay },
  addCandidateNote: { component: CandidateOperationDisplay },
  importCandidateFromResume: { component: CandidateOperationDisplay },
  importCandidateFromText: { component: CandidateOperationDisplay },
  generateResume: { component: ResumeGeneratorDisplay },

  // ============== Candidates - Skills ==============
  addCandidateSkill: { component: CandidateOperationDisplay },
  removeCandidateSkill: { component: CandidateOperationDisplay },

  // ============== Candidates - Roles ==============
  addCandidateRole: { component: CandidateOperationDisplay },
  updateCandidateRole: { component: CandidateOperationDisplay },
  removeCandidateRole: { component: CandidateOperationDisplay },

  // ============== Candidates - Projects ==============
  addCandidateProject: { component: CandidateOperationDisplay },
  updateCandidateProject: { component: CandidateOperationDisplay },
  removeCandidateProject: { component: CandidateOperationDisplay },

  // ============== Candidates - Education ==============
  addCandidateEducation: { component: CandidateOperationDisplay },
  updateCandidateEducation: { component: CandidateOperationDisplay },
  removeCandidateEducation: { component: CandidateOperationDisplay },

  // ============== Contacts ==============
  searchContacts: { component: SearchContactsDisplay },
  getContact: { component: ContactDetailDisplay },
  createContact: { component: ContactOperationDisplay },
  updateContact: { component: ContactOperationDisplay },
  deleteContact: { component: ContactOperationDisplay },

  // ============== Floats - Core ==============
  searchFloats: { component: SearchFloatsDisplay },
  getFloat: { component: FloatDetailDisplay },
  createFloat: { component: FloatOperationDisplay },
  updateFloat: { component: FloatOperationDisplay },
  deleteFloat: { component: FloatOperationDisplay },

  // ============== Floats - Utilities ==============
  fetchCandidatesForFloat: { component: SearchCandidatesDisplay },
  duplicateFloat: { component: FloatOperationDisplay },

  // ============== Floats - Engagement ==============
  getFloatInteractions: { component: ListResultsDisplay },
  createShareLink: { component: FloatOperationDisplay },

  // ============== Research ==============
  searchWeb: { component: WebSearchDisplay, alwaysShowRich: true },

  // ============== Email Campaigns ==============
  getEmailDraft: { component: EmailDraftDisplay },
  createFloatEmail: { component: EmailCampaignDisplay },
  sendTestEmail: { component: EmailCampaignDisplay },
  sendEmailCampaign: { component: EmailCampaignDisplay, alwaysShowRich: (call) => isPendingSend(call.finalOutput) },
  createSubmissionEmail: { component: EmailCampaignDisplay },
  sendSubmissionEmail: { component: EmailCampaignDisplay, alwaysShowRich: (call) => isPendingSend(call.finalOutput) },
  createScheduledMessage: { component: EmailCampaignDisplay },
  getScheduledMessages: { component: ListResultsDisplay },

  // ============== Dashboard & Stats ==============
  getDashboardStats: { component: DashboardStatsDisplay },
  getTeamDashboardStats: { component: DashboardStatsDisplay },
  getTopLeads: { component: TopLeadsDisplay },

  // ============== Integrations ==============
  getIntegrationStatus: { component: IntegrationStatusDisplay },
  disconnectIntegration: { component: IntegrationStatusDisplay },
  proxyIntegrationRequest: { component: SimpleDisplay },
  clearIntegrationCache: { component: OperationSuccessDisplay },

  // ============== Team ==============
  getTeamCandidates: { component: TeamDataDisplay },
  getTeamCandidate: { component: CandidateDetailDisplay },
  duplicateTeamCandidate: { component: CandidateOperationDisplay },
  getTeamFloats: { component: TeamDataDisplay },
  getTeamFloat: { component: FloatDetailDisplay },
  duplicateTeamFloat: { component: FloatOperationDisplay },
  getTeamContacts: { component: TeamDataDisplay },
  getTeamContact: { component: ContactDetailDisplay },
  duplicateTeamContact: { component: ContactOperationDisplay },

  // ============== Notifications ==============
  getNotifications: { component: NotificationsDisplay },
  markNotificationsRead: { component: OperationSuccessDisplay },

  // ============== Tags ==============
  getTags: { component: ListResultsDisplay },

  // ============== Quality Control ==============
  judgeConversation: { component: JudgeResultDisplay },

  // ============== Reports ==============
  writeAnalysisReport: { component: SimpleDisplay },

  // ============== Suggestions ==============
  suggestFollowUps: { component: QuickRepliesDisplay, alwaysShowRich: true },
};

/**
 * Get the display component for a tool
 *
 * @param toolName - The name of the tool
 * @returns The display component (sync or lazy) and whether it's lazy
 */
export function getToolDisplay(toolName: string): {
  component: ComponentType<ToolDisplayProps>;
  lazy: boolean;
} {
  const entry = toolDisplayRegistry[toolName as ToolName];

  if (!entry) {
    // Default to SimpleDisplay for unknown tools
    return { component: SimpleDisplay, lazy: false };
  }

  if (entry.lazy) {
    // For lazy components, we'll need to handle them specially in ToolRenderer
    return { component: entry.component as ComponentType<ToolDisplayProps>, lazy: true };
  }

  return { component: entry.component as ComponentType<ToolDisplayProps>, lazy: false };
}

/**
 * Check if a tool call should show its rich display even when debug mode is OFF
 */
export function shouldAlwaysShowRich(toolCall: Pick<AggregatedToolCall, "toolName"> & Partial<AggregatedToolCall>): boolean {
  const rule = toolDisplayRegistry[toolCall.toolName as ToolName]?.alwaysShowRich;
  return typeof rule === "function" ? rule(toolCall as AggregatedToolCall) : rule === true;
}

/**
 * Check if a tool has a custom display component (not SimpleDisplay)
 */
export function hasCustomDisplay(toolName: string): boolean {
  const entry = toolDisplayRegistry[toolName as ToolName];
  if (!entry) return false;

  // Check if it's something other than SimpleDisplay
  return entry.component !== SimpleDisplay;
}

/**
 * Get list of all registered tools
 */
export function getRegisteredTools(): string[] {
  return Object.keys(toolDisplayRegistry);
}

/**
 * Default display component
 */
export const DefaultDisplay = SimpleDisplay;
